import { parseISO } from "date-fns";
import { z } from "zod";
import { ApiError } from "@/core/api/apiTypes";

const SubscriptionFeatureSchema = z.enum(["sms", "ai"]);
const SubscriptionSkuSchema = z.enum(["pro_v1", "pro_ai_v1"]);
const SubscriptionStatusSchema = z.enum([
  "incomplete", "incomplete_expired", "trialing", "active", "past_due", "canceled", "unpaid", "paused",
]);

const SubscriptionUsageSchema = z.object({
  metric: z.literal("sms"),
  used: z.number().int().nonnegative(),
  limit: z.number().int().nonnegative(),
  resetsAt: z.date(),
});
const SubscriptionOfferSchema = z.object({
  sku: SubscriptionSkuSchema,
  name: z.string(),
  features: z.array(SubscriptionFeatureSchema),
  limits: z.object({ sms: z.number().int().nonnegative() }),
  price: z.object({ amountMinor: z.number().int().nonnegative(), currency: z.string(), interval: z.literal("month") }),
});
export const SubscriptionSchema = z.object({
  sku: z.string().nullable(),
  name: z.string().nullable(),
  status: SubscriptionStatusSchema.nullable(),
  currentPeriodEnd: z.date().nullable(),
  cancelAtPeriodEnd: z.boolean(),
  features: z.array(SubscriptionFeatureSchema),
  usage: z.array(SubscriptionUsageSchema),
  manageable: z.boolean(),
  offers: z.array(SubscriptionOfferSchema),
});

const SubscriptionDtoSchema = z.object({
  sku: z.string().nullable(),
  name: z.string().nullable(),
  status: SubscriptionStatusSchema.nullable(),
  current_period_end: z.iso.datetime({ offset: true }).nullable(),
  cancel_at_period_end: z.boolean(),
  features: z.array(SubscriptionFeatureSchema),
  usage: z.array(z.object({
    metric: z.literal("sms"),
    used: z.number().int().nonnegative(),
    limit: z.number().int().nonnegative(),
    period_start: z.string(),
    resets_at: z.iso.datetime({ offset: true }),
  })),
  manageable: z.boolean(),
  offers: z.array(z.object({
    sku: SubscriptionSkuSchema,
    name: z.string(),
    features: z.array(SubscriptionFeatureSchema),
    limits: z.object({ sms: z.number().int().nonnegative() }),
    price: z.object({ amount_minor: z.number().int().nonnegative(), currency: z.string(), interval: z.literal("month") }),
  })),
});
const SubscriptionPaymentRequiredSchema = z.union([
  z.object({ code: z.literal("feature_required"), feature: SubscriptionFeatureSchema }),
  z.object({ code: z.literal("quota_exceeded"), metric: z.literal("sms") }),
]);

export type SubscriptionDto = z.infer<typeof SubscriptionDtoSchema>;
export type SubscriptionFeature = z.infer<typeof SubscriptionFeatureSchema>;
export type SubscriptionSku = z.infer<typeof SubscriptionSkuSchema>;
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>;
export type SubscriptionUsage = z.infer<typeof SubscriptionUsageSchema>;
export type SubscriptionOffer = z.infer<typeof SubscriptionOfferSchema>;
export type Subscription = z.infer<typeof SubscriptionSchema>;
export type SubscriptionPaymentRequired = z.infer<typeof SubscriptionPaymentRequiredSchema>;

export function fromSubscriptionDtoToSubscription(dto: SubscriptionDto): Subscription {
  const subscriptionDto = SubscriptionDtoSchema.parse(dto);
  return {
    sku: subscriptionDto.sku,
    name: subscriptionDto.name,
    status: subscriptionDto.status,
    currentPeriodEnd: subscriptionDto.current_period_end ? parseISO(subscriptionDto.current_period_end) : null,
    cancelAtPeriodEnd: subscriptionDto.cancel_at_period_end,
    features: subscriptionDto.features,
    usage: subscriptionDto.usage.map((usage) => ({
      metric: usage.metric,
      used: usage.used,
      limit: usage.limit,
      resetsAt: parseISO(usage.resets_at),
    })),
    manageable: subscriptionDto.manageable,
    offers: subscriptionDto.offers.map((offer) => ({
      sku: offer.sku,
      name: offer.name,
      features: offer.features,
      limits: { sms: offer.limits.sms },
      price: { amountMinor: offer.price.amount_minor, currency: offer.price.currency, interval: offer.price.interval },
    })),
  };
}

/** The 402 body the API returns when a plan does not include a feature or its monthly limit is used up. */
export function getSubscriptionPaymentRequired(error: unknown): SubscriptionPaymentRequired | null {
  if (!(error instanceof ApiError) || error.Status !== 402) return null;
  const parsed = SubscriptionPaymentRequiredSchema.safeParse(error.Data);
  return parsed.success ? parsed.data : null;
}
